"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShoppingCart, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { type PreOrderOutput, PreOrderStatus } from "@/interfaces/pre-orders";
import { orderPaymentDetailsMethodTranslations } from "@/interfaces/payments";
import { numberToMoney } from "@/util/moneyConversion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { orderDeliveryTypeTransalation } from "@/interfaces/orders";
import { localDate } from "@/util/dates";
import { formatCep, formatDddPhone } from "@/util/formatters";

interface PreOrderDetailsViewProps {
  preOrder: PreOrderOutput;
  onBack: () => void;
}

export function PreOrderDetailsView({
  preOrder,
  onBack,
}: PreOrderDetailsViewProps) {
  // Format the date
  const formattedDate = format(
    new Date(preOrder.createdAt),
    "dd/MM/yyyy HH:mm",
    { locale: ptBR }
  );

  // Function to safely render HTML content
  const renderHTML = (htmlContent?: string) => {
    if (!htmlContent) return null;
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="mr-2 text-gray-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-medium text-gray-800">
            Detalhes do Pré-Pedido
          </h2>
        </div>

        {/* Status Badge */}
        {preOrder.status === PreOrderStatus.ACCEPTED ? (
          <Badge
            variant="outline"
            className="text-green-600 bg-transparent border-green-200"
          >
            Aceito
          </Badge>
        ) : preOrder.status === PreOrderStatus.REJECTED ? (
          <Badge
            variant="outline"
            className="text-red-600 bg-transparent border-red-200"
          >
            Recusado
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-amber-600 bg-transparent border-amber-200"
          >
            Pendente
          </Badge>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b pb-3">
            <CardTitle className="text-base font-medium text-gray-700">
              Informações do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">Nome</span>
              <span className="font-medium text-base">
                {preOrder.customer.name}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">
                Telefone
              </span>
              <span className="font-medium text-base">
                {formatDddPhone(
                  preOrder.customer.ddd,
                  preOrder.customer.phoneNumber,
                  preOrder.customer.internationalCode
                ) || "-"}
              </span>
            </div>

            {preOrder.delivery.deliveryType === "DELIVERY" &&
              preOrder.delivery.address && (
                <div className="mt-2">
                  <h4 className="text-xs text-gray-500 font-medium mb-2">
                    Endereço de Entrega
                  </h4>
                  <div className="p-3 border border-gray-100 rounded-md bg-gray-50">
                    <p className="font-medium text-sm">
                      {preOrder.delivery.address.line1},{" "}
                      {preOrder.delivery.address.number}
                      {preOrder.delivery.address.line2 &&
                        `, ${preOrder.delivery.address.line2}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {preOrder.delivery.address.neighborhood},{" "}
                      {preOrder.delivery.address.city}
                    </p>
                    {preOrder.delivery.address.zipCode && (
                      <p className="text-xs text-gray-500">
                        CEP: {formatCep(preOrder.delivery.address.zipCode)}
                      </p>
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b pb-3">
            <CardTitle className="text-base font-medium text-gray-700">
              Detalhes do Pedido
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Tipo de Entrega
                </span>
                <span className="font-medium text-base">
                  {
                    orderDeliveryTypeTransalation[
                      preOrder.delivery.deliveryType
                    ]
                  }
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Método de Pagamento
                </span>
                <span className="font-medium text-base">
                  {
                    orderPaymentDetailsMethodTranslations[
                      preOrder.paymentMethod
                    ]
                  }
                </span>
              </div>

              {preOrder.delivery?.deliveryAt && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 font-medium">
                    Data de Entrega
                  </span>
                  <span className="font-medium text-base">
                    {format(
                      localDate(preOrder.delivery!.deliveryAt!),
                      "dd/MM/yyyy - HH:mm:ss",
                      { locale: ptBR }
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">
                Data de Criação
              </span>
              <span className="font-medium text-base">{formattedDate}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium">Código</span>
              <span className="font-medium text-base">{preOrder.code}</span>
            </div>

            {preOrder.observation && (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">
                  Observação
                </span>
                <div className="font-medium text-sm prose prose-sm max-w-none">
                  {renderHTML(preOrder.observation)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <ShoppingCart className="h-4 w-4 mr-2 text-gray-500" />
            Itens do Pedido
          </CardTitle>
          <Badge
            variant="outline"
            className="bg-transparent text-gray-600 border-gray-200"
          >
            {(preOrder.items || []).length}{" "}
            {(preOrder.items || []).length === 1 ? "item" : "itens"}
          </Badge>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            {(preOrder.items || []).map((item, index) => (
              <div
                key={index}
                className="p-4 border border-gray-100 rounded-md bg-gray-50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-medium text-base">{item.name}</h3>
                      <Badge
                        variant="outline"
                        className="ml-2 text-blue-600 bg-transparent border-blue-200 text-xs"
                      >
                        {item.kind === "OFFER" ? "Oferta" : "Produto"}
                      </Badge>
                    </div>
                    {item.description && (
                      <div className="text-sm text-gray-500 prose prose-sm max-w-none">
                        {renderHTML(item.description)}
                      </div>
                    )}
                    {item.observation && (
                      <p className="text-xs text-gray-500 italic mt-1">
                        Obs: {item.observation}
                      </p>
                    )}

                    {/* Exibir sub-itens se existirem */}
                    {item.items &&
                      Array.isArray(item.items) &&
                      item.items.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            Itens inclusos:
                          </p>
                          {item.items.map((subItem, idx) => (
                            <div
                              key={idx}
                              className="pl-4 border-l-2 border-gray-200"
                            >
                              <p className="font-medium text-sm">
                                {subItem.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Preço unitário:{" "}
                                {numberToMoney(subItem.unitPrice)} • Qtd:{" "}
                                {subItem.quantity}
                              </p>

                              {/* Exibir adicionais do sub-item */}
                              {subItem.additionals &&
                                subItem.additionals.length > 0 && (
                                  <div className="mt-1 ml-2">
                                    <p className="text-xs font-medium text-gray-500 mb-1">
                                      Adicionais:
                                    </p>
                                    {subItem.additionals.map(
                                      (additional, addIdx) => (
                                        <div
                                          key={addIdx}
                                          className="text-xs text-gray-400 ml-2"
                                        >
                                          • {additional.name}{" "}
                                          {additional.unitPrice > 0 &&
                                            `(+${numberToMoney(
                                              additional.unitPrice
                                            )})`}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Exibir adicionais diretos do item principal */}
                    {item.additionals &&
                      Array.isArray(item.additionals) &&
                      item.additionals.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-1 mb-2">
                            <span className="text-sm font-semibold text-gray-800">
                              Adicionais ({item.additionals.length})
                            </span>
                          </div>
                          {item.additionals.map((additional, addIdx) => (
                            <div
                              key={addIdx}
                              className="bg-orange-50 border border-orange-100 p-3 rounded-lg"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900 text-sm">
                                      {additional.name}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Qtd: {additional.quantity}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <span>Preço unitário: {numberToMoney(additional.unitPrice)}</span>
                                  </div>
                                </div>
                                <div className="text-right ml-3">
                                  <div className="font-bold text-orange-600 text-sm">
                                    {numberToMoney(additional.unitPrice * additional.quantity)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Total adicional
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="text-right">
                    <div className="flex flex-col items-end">
                      <Badge
                        variant="outline"
                        className="mb-1 text-blue-600 bg-transparent border-blue-200 text-xs"
                      >
                        {item.quantity}{" "}
                        {item.quantity === 1 ? "unidade" : "unidades"}
                      </Badge>
                      <p className="font-medium text-sm">
                        {item.quantity} x {numberToMoney(item.unitPrice)}
                      </p>
                      <p className="font-bold text-base">
                        {numberToMoney(item.quantity * item.unitPrice)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {preOrder.tax && preOrder.tax > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-100 flex justify-between items-center">
              <span className="text-base font-medium">Imposto:</span>
              <span className="text-base font-medium">
                {numberToMoney(preOrder.tax)}
              </span>
            </div>
          )}
          <div className="mt-2 p-4 bg-gray-50 rounded-md border border-gray-100 flex justify-between items-center">
            <span className="text-base font-medium">Total do Pedido:</span>
            <span className="text-xl font-bold">
              {numberToMoney(preOrder.totalAmount)}
            </span>
          </div>
        </CardContent>
      </Card>

      {preOrder.status === PreOrderStatus.ACCEPTED && (
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2 text-gray-800">
                Pré-Pedido Aceito
              </h3>
              <p className="text-gray-600 max-w-md">
                Este pré-pedido foi aceito e convertido em um pedido no sistema.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {preOrder.status === PreOrderStatus.REJECTED && (
        <Card className="border-0 shadow-sm bg-red-50">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center">
              <XCircle className="h-12 w-12 text-red-500 mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2 text-gray-800">
                Pré-Pedido Recusado
              </h3>
              <p className="text-gray-600 max-w-md">
                Este pré-pedido foi recusado e não foi convertido em um pedido.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-start pt-4">
        <Button
          onClick={onBack}
          className="border-gray-200 bg-gray-800 hover:bg-gray-900 text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar para Lista
        </Button>
      </div>
    </div>
  );
}
